import { useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Search, Sparkles, ArrowRight, SearchX, Hash } from 'lucide-react';
import { Badge, Button, Card, CardContent, cn } from '@devkit/ui';
import {
  getAllTools,
  getCategories,
  getCategoryById,
  getPopularTools,
  type ToolCategory,
  type ToolDefinition,
  SITE,
} from '../lib/toolRegistry';
import { SEOHead } from '../components/seo/SEOHead';
import { buildBreadcrumbLd, buildItemListLd } from '../lib/seoJsonLd';

type FilterId = 'all' | ToolCategory;

const CATEGORY_COLOR: Record<string, string> = {
  json: `text-yellow-400 bg-yellow-400/10`,
  encoding: `text-blue-400 bg-blue-400/10`,
  security: `text-purple-400 bg-purple-400/10`,
  regex: `text-green-400 bg-green-400/10`,
  time: `text-orange-400 bg-orange-400/10`,
  text: `text-cyan-400 bg-cyan-400/10`,
  utility: `text-pink-400 bg-pink-400/10`,
};

export default function ToolsPage() {
  const params = useParams();
  const categoryParam = (params.category ?? '') as ToolCategory | '';
  const validCategory: ToolCategory | '' = categoryParam && getCategoryById(categoryParam)
    ? categoryParam
    : '';

  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState<FilterId>(
    (validCategory as FilterId) || 'all'
  );

  const allTools = getAllTools();
  const popular = getPopularTools();
  const categories = getCategories();
  const totalCount = allTools.length;

  // Pill list: All + categories with ≥1 active tool.
  const pills: Array<{ id: FilterId; name: string }> = [
    { id: 'all', name: 'All' },
    ...categories
      .filter((c) => allTools.some((t) => t.category === c.id))
      .map((c) => ({ id: c.id as FilterId, name: c.name.replace(' Tools', '') })),
  ];

  const counts = useMemo(() => {
    const map: Record<string, number> = { all: totalCount };
    for (const t of allTools) {
      map[t.category] = (map[t.category] ?? 0) + 1;
    }
    return map;
  }, [allTools, totalCount]);

  const filtered = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return allTools.filter((tool) => {
      const matchesCategory = activeCategory === 'all' || tool.category === activeCategory;
      if (!matchesCategory) return false;
      if (!q) return true;
      const haystack = [
        tool.name,
        tool.shortDescription,
        tool.longDescription,
        tool.category,
        ...tool.keywords,
      ]
        .join(' ')
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [allTools, activeCategory, searchQuery]);

  const isFiltered = activeCategory !== 'all' || searchQuery.trim() !== '';

  const activeCat = activeCategory !== 'all' ? getCategoryById(activeCategory as ToolCategory) : undefined;
  const pageTitle = activeCat
    ? `${activeCat.name} — DevKit`
    : `All Developer Tools — DevKit`;
  const pageDescription = activeCat
    ? `${activeCat.description} Browse ${counts[activeCategory] ?? 0} free ${activeCat.name.toLowerCase()} that run in your browser.`
    : `Browse all ${totalCount} free developer tools. JSON formatter, JWT decoder, Base64 encoder, regex tester, UUID generator and more. All run in your browser.`;

  const canonical = activeCat
    ? `${SITE.url}/tools/${activeCat.id}`
    : `${SITE.url}/tools`;
  const jsonLd = [
    buildBreadcrumbLd([
      { name: `Home`, url: SITE.url },
      { name: `Tools`, url: `${SITE.url}/tools` },
      ...(activeCat ? [{ name: activeCat.name, url: `${SITE.url}/tools/${activeCat.id}` }] : []),
    ]),
    buildItemListLd(
      activeCat ? `${activeCat.name} tools` : `All DevKit tools`,
      filtered.slice(0, 50).map((t) => ({
        name: t.name,
        url: `${SITE.url}/${t.slug}`,
        description: t.shortDescription,
      }))
    ),
  ];

  return (
    <div className="animate-fade-in">
      <SEOHead
        title={pageTitle}
        description={pageDescription}
        canonical={canonical}
        og={{ title: pageTitle, description: pageDescription, url: canonical, type: `website` }}
        twitter={{ card: `summary_large_image`, title: pageTitle, description: pageDescription }}
        jsonLd={jsonLd}
      />

      {/* Hero */}
      <section className="relative py-16 md:py-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-primary/5 via-transparent to-transparent" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] bg-primary/10 rounded-full blur-3xl" />

        <div className="container mx-auto px-4 relative">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-3xl md:text-5xl font-bold text-white mb-4 text-balance">
              {activeCat ? (
                <>
                  {activeCat.name.replace(' Tools', '')}{' '}
                  <span className="gradient-text">Tools</span>
                </>
              ) : (
                <>
                  Developer <span className="gradient-text">Tools</span>
                </>
              )}
            </h1>

            <p className="text-base md:text-lg text-slate-400 max-w-2xl mx-auto">
              {activeCat
                ? activeCat.description
                : `Fast, private, browser-based utilities. Your data never leaves your device.`}
            </p>
          </div>
        </div>
      </section>

      {/* Search */}
      <section className="pb-6">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400 pointer-events-none" />
              <input
                type="text"
                placeholder="Search tools by name, description or keyword..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className={cn(
                  'w-full pl-12 pr-4 py-3 bg-surface border border-slate-700/60 rounded-xl',
                  'text-slate-100 placeholder:text-slate-500',
                  'focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20',
                  'transition-all'
                )}
                aria-label="Search tools"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Category pills */}
      <section className="pb-10">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="flex items-center gap-2 overflow-x-auto pb-2 -mx-1 px-1 scrollbar-thin">
              {pills.map((cat) => {
                const isActive = activeCategory === cat.id;
                return (
                  <button
                    key={cat.id}
                    onClick={() => setActiveCategory(cat.id)}
                    className={cn(
                      'inline-flex items-center gap-2 px-3.5 py-1.5 text-sm font-medium rounded-full whitespace-nowrap transition-all',
                      isActive
                        ? 'bg-primary text-white shadow-sm shadow-primary/30'
                        : 'bg-surface text-slate-300 hover:text-white border border-slate-700/60 hover:border-slate-600'
                    )}
                    aria-pressed={isActive}
                  >
                    {cat.name}
                    <span
                      className={cn(
                        'text-xs px-1.5 py-0.5 rounded-full',
                        isActive ? 'bg-white/20 text-white' : 'bg-slate-800 text-slate-400'
                      )}
                    >
                      {counts[cat.id] ?? 0}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Popular — only when not filtering */}
      {!isFiltered && (
        <section className="pb-12">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <div className="flex items-center gap-2 mb-5">
                <Sparkles className="w-4 h-4 text-primary" />
                <h2 className="text-sm font-semibold text-slate-300 uppercase tracking-wider">
                  Popular
                </h2>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                {popular.map((tool) => (
                  <ToolCard key={tool.slug} tool={tool} compact />
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* All / filtered tools */}
      <section className="pb-20">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-sm font-semibold text-slate-300 uppercase tracking-wider">
                {isFiltered ? `Results` : `All Tools`}
                <span className="ml-2 text-xs font-normal text-slate-500 normal-case tracking-normal">
                  {filtered.length} {filtered.length === 1 ? `tool` : `tools`}
                </span>
              </h2>
              {isFiltered && (
                <button
                  onClick={() => {
                    setActiveCategory('all');
                    setSearchQuery('');
                  }}
                  className="text-sm text-primary hover:underline"
                >
                  Clear filters
                </button>
              )}
            </div>

            {filtered.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                {filtered.map((tool) => (
                  <ToolCard key={tool.slug} tool={tool} />
                ))}
              </div>
            ) : (
              <EmptyState
                onClear={() => {
                  setActiveCategory('all');
                  setSearchQuery('');
                }}
              />
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

function ToolCard({ tool, compact = false }: { tool: ToolDefinition; compact?: boolean }) {
  const color = CATEGORY_COLOR[tool.category] ?? `text-slate-400 bg-slate-400/10`;

  if (compact) {
    return (
      <Link to={`/${tool.slug}`} className="group">
        <Card className="card-hover h-full">
          <CardContent className="p-4 flex flex-col items-center text-center">
            <div className={`w-11 h-11 rounded-xl flex items-center justify-center mb-3 ${color}`}>
              <Hash className="w-5 h-5" />
            </div>
            <h3 className="font-medium text-white text-sm">{tool.name}</h3>
          </CardContent>
        </Card>
      </Link>
    );
  }

  return (
    <Link to={`/${tool.slug}`} className="group">
      <Card className="card-hover h-full">
        <CardContent className="p-6">
          <div className="flex items-start gap-4">
            <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${color}`}>
              <Hash className="w-5 h-5" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                <h3 className="font-semibold text-white">{tool.name}</h3>
                {tool.popular && (
                  <Badge variant="primary" className="text-[10px] py-0">
                    Popular
                  </Badge>
                )}
              </div>
              <p className="text-sm text-slate-400 line-clamp-2">{tool.shortDescription}</p>
              <div className="mt-3 flex items-center justify-between">
                <Badge variant="default" className="capitalize">
                  {tool.category}
                </Badge>
                <ArrowRight className="w-4 h-4 text-slate-500 group-hover:text-primary group-hover:translate-x-0.5 transition-all" />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}

function EmptyState({ onClear }: { onClear: () => void }) {
  return (
    <div className="text-center py-16 px-4 border border-dashed border-slate-700/60 rounded-2xl bg-surface/30">
      <div className="w-14 h-14 rounded-2xl bg-surface flex items-center justify-center mx-auto mb-4">
        <SearchX className="w-7 h-7 text-slate-500" />
      </div>
      <h3 className="text-lg font-semibold text-white mb-1">No tools found</h3>
      <p className="text-sm text-slate-400 mb-5 max-w-sm mx-auto">
        Try a different keyword or clear the active filter to see all available tools.
      </p>
      <Button variant="outline" size="sm" onClick={onClear}>
        Clear filters
      </Button>
    </div>
  );
}